import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  ChevronLeftIcon, 
  DocumentIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import { Button } from '../Button/Button';
import { Checkbox } from '../Checkbox/Checkbox';
import { FileUploader } from '../FileUploader/FileUploader';
import { AlertDialog } from '../AlertDialog/AlertDialog';
import type { FileUploaderGalleryProps, GalleryResource } from './FileUploaderGallery.types';
import { DEFAULT_ACCEPTED_EXTENSIONS, DEFAULT_MAX_FILE_SIZE_MB, DEFAULT_MAX_TOTAL_SIZE_MB } from './FileUploaderGallery.types';

/**
 * FileUploaderGallery Item Component with internal loading, error and deletion states
 */
const GalleryItem: React.FC<{
  resource: GalleryResource;
  allowSelection: boolean;
  allowDeletion: boolean;
  onResourceCheck?: any;
  onDeleteRequest: (resource: GalleryResource) => void;
  isImage: (type: string) => boolean;
  showFileName?: boolean;
}> = ({ 
  resource, 
  allowSelection, 
  allowDeletion, 
  onResourceCheck, 
  onDeleteRequest, 
  isImage, 
  showFileName 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const esImg = isImage(resource.type);
  const shouldShowName = showFileName ?? !esImg;

  return (
    <div className="group relative flex flex-col gap-2 animate-in zoom-in-95 duration-200">
      <div className="relative aspect-[3/4] w-full bg-gray-100 dark:bg-gray-800 rounded-lg border border-border-primary dark:border-dark-border-primary overflow-hidden">
        {/* Individual Skeleton while image loads and no error */}
        {esImg && !isLoaded && !hasError && (
          <div className="absolute inset-0 z-0 animate-pulse bg-gray-200 dark:bg-gray-700" />
        )}

        {hasError ? (
          /* Error State UI */
          <div className="size-full flex flex-col items-center justify-center gap-2 bg-gray-50 dark:bg-gray-900/40 p-4 transition-colors">
            <div className="relative">
              <PhotoIcon className="size-10 text-gray-300 dark:text-gray-600" />
              <div className="absolute -top-1 -right-1 size-4 bg-white dark:bg-dark-bg-primary rounded-full flex items-center justify-center">
                <XMarkIcon className="size-3 text-red-500" />
              </div>
            </div>
            <span className="text-[10px] leading-tight text-gray-400 dark:text-gray-500 font-medium text-center uppercase tracking-wider">
              Recurso no disponible
            </span>
          </div>
        ) : esImg ? (
          <img 
            src={resource.url} 
            alt={resource.name} 
            onLoad={() => setIsLoaded(true)}
            onError={() => {
              setHasError(true);
              setIsLoaded(true);
            }}
            className={`size-full object-cover transition-all duration-500 group-hover:scale-105 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ) : (
          <div className="size-full flex items-center justify-center">
            <DocumentIcon className="size-12 text-gray-400 dark:text-gray-500" />
          </div>
        )}

        {/* Checkbox Overlay */}
        {allowSelection && (
          <div className="absolute top-2 left-2 z-10">
            <Checkbox 
              checked={resource.checked}
              onChange={(e) => onResourceCheck?.(resource.uuid, e.target.checked)}
              className="!p-0"
            />
          </div>
        )}

        {/* Delete Overlay */}
        {allowDeletion && (
          <div className="absolute top-2 right-2 z-10">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteRequest(resource);
              }}
              className="p-1 bg-white/80 dark:bg-dark-bg-primary/80 backdrop-blur-sm rounded-full border border-red-200 dark:border-red-900 shadow-sm hover:bg-red-50 dark:hover:bg-red-900/40 transition-all active:scale-90"
              title="Eliminar recurso"
              aria-label="Eliminar recurso"
            >
              <XMarkIcon className="size-4 text-red-600 dark:text-red-400" />
            </button>
          </div>
        )}
      </div>

      {shouldShowName && (
        <span 
          className="text-sm font-medium text-content-primary dark:text-dark-content-primary truncate px-1"
          title={resource.name}
        >
          {resource.name}
        </span>
      )}
    </div>
  );
};

/**
 * FileUploaderGallery from the Siesa design system
 */
export const FileUploaderGallery: React.FC<FileUploaderGalleryProps> = ({
  title,
  entity,
  entityId,
  mode = 'modal',
  isMaximized = false,
  resources = [],
  allowSelection = false,
  acceptButtonText = 'Aceptar',
  showFileUploader = true,
  showFileName,
  isLoading = false,
  allowDeletion = false,
  error = null,
  onAccept,
  onClose,
  onBack,
  onUpload,
  onDelete,
  onResourceCheck,
  className = '',
  embedded = false,
  acceptedExtensions = DEFAULT_ACCEPTED_EXTENSIONS,
  maxFileSizeMB = DEFAULT_MAX_FILE_SIZE_MB,
  maxTotalSizeMB = DEFAULT_MAX_TOTAL_SIZE_MB,
  multipleFiles = true,
}) => {
  const [resourceToDelete, setResourceToDelete] = useState<GalleryResource | null>(null);
  const [internalError, setInternalError] = useState<{ title: string; message: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Sync internal error state with prop
  useEffect(() => {
    if (error) {
      setInternalError(error);
    } else {
      setInternalError(null);
    }
  }, [error]);

  // ===== IMAGE DETECTION LOGIC =====
  const isImage = (type: string) => {
    return type.startsWith('image/') || 
           /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(type);
  };

  // ===== SKELETON RENDERER (Global) =====
  const renderSkeletons = () => {
    return Array.from({ length: 4 }).map((_, i) => (
      <div key={`skeleton-${i}`} className="flex flex-col gap-2 animate-pulse">
        <div className="aspect-[3/4] w-full bg-gray-200 dark:bg-gray-800 rounded-lg border border-border-primary dark:border-dark-border-primary" />
        <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-800 rounded px-1" />
      </div>
    ));
  };

  // ===== UPLOAD LOGIC =====
  const handleUploadFunction = async (file: File, onProgress: (p: number) => void) => {
    // Validar tamaño individual
    const maxBytes = maxFileSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      setInternalError({
        title: 'Archivo demasiado grande',
        message: `El archivo "${file.name}" supera el límite de ${maxFileSizeMB} MB por archivo.`,
      });
      throw new Error('file-too-large');
    }

    // Validar tamaño acumulado con los archivos ya existentes
    const currentTotalBytes = resources.reduce((sum, r) => sum + (r.size ?? 0), 0);
    if (currentTotalBytes + file.size > maxTotalSizeMB * 1024 * 1024) {
      setInternalError({
        title: 'Límite de almacenamiento superado',
        message: `El tamaño total de archivos no puede superar ${maxTotalSizeMB} MB. Elimina archivos existentes antes de continuar.`,
      });
      throw new Error('total-size-exceeded');
    }

    if (onUpload) {
      onProgress(50);
      await onUpload(file);
      onProgress(100);
      return { url: URL.createObjectURL(file), name: file.name };
    }
    onProgress(100);
    return URL.createObjectURL(file);
  };

  // ===== DELETE LOGIC (Optimistic) =====
  const confirmDelete = async () => {
    if (!resourceToDelete || !onDelete) return;

    const resourceId = resourceToDelete.uuid;
    setResourceToDelete(null);
    
    try {
      setIsDeleting(true);
      await onDelete(resourceId);
    } catch (err) {
      console.error('Error al eliminar recurso:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // ===== MAIN CONTAINER CLASSES =====
  const containerClasses = [
    mode === 'modal' ? 'fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4' : 'w-full h-full',
    'animate-in fade-in duration-200',
    className
  ].join(' ').trim();

  // ===== MODAL/PANEL CLASSES =====
  const panelClasses = [
    'bg-white dark:bg-dark-bg-primary',
    'border border-border-primary dark:border-dark-border-primary',
    'flex flex-col overflow-hidden',
    mode === 'modal' ? (isMaximized ? 'w-full h-full rounded-none' : 'w-full max-w-[800px] max-h-[90vh] rounded-2xl shadow-2xl') : 'w-full h-full rounded-none'
  ].join(' ').trim();

  return (
    <div className={containerClasses}>
      <div className={panelClasses}>
        {/* ===== HEADER ===== */}
        {!embedded && (
          <div className="bg-[#f4f4f5] dark:bg-dark-bg-primary/50 border-b border-border-primary dark:border-dark-border-primary px-5 py-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-4">
              {mode === 'page' && onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
                  aria-label="Volver"
                >
                  <ChevronLeftIcon className="size-6 text-content-primary dark:text-dark-content-primary" />
                </button>
              )}
              <h3 className="text-[20px] font-bold text-content-primary dark:text-dark-content-primary leading-7 tracking-tight">
                {title}
              </h3>
            </div>

            {mode === 'modal' && (
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 border border-[#93d1fd] dark:border-dark-border-custom bg-white dark:bg-dark-bg-primary rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                aria-label="Cerrar"
              >
                <XMarkIcon className="size-4 text-primary-custom-600 dark:text-primary-custom-400" />
              </button>
            )}
          </div>
        )}

        {/* ===== BODY ===== */}
        <div className="flex-1 overflow-y-auto p-5 scrollbar-thin scrollbar-thumb-border-primary dark:scrollbar-thumb-dark-border-primary">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 items-stretch">
            {/* FileUploader Slot */}
            {showFileUploader && (
              <div className="col-span-2">
                <FileUploader
                  variant="dropzone"
                  uploadFunction={handleUploadFunction}
                  label="Subir archivo"
                  subLabel="o arrastra y suelta aquí"
                  accept={acceptedExtensions}
                  maxSize={maxFileSizeMB * 1024 * 1024}
                  multiple={multipleFiles}
                  className="h-full"
                  classNames={{
                    dropzoneInactive: 'h-full min-h-[180px] flex flex-col justify-center',
                    dropzoneActive: 'h-full min-h-[180px] flex flex-col justify-center'
                  }}
                />
              </div>
            )}

            {/* Skeletons while loading */}
            {isLoading && renderSkeletons()}

            {/* Resources List */}
            {!isLoading && resources.map((resource) => (
              <GalleryItem 
                key={resource.uuid}
                resource={resource}
                allowSelection={allowSelection}
                allowDeletion={allowDeletion}
                onDeleteRequest={(r) => setResourceToDelete(r)}
                onResourceCheck={(uuid: string, checked: boolean) => {
                  const updated = resources.map(r => r.uuid === uuid ? { ...r, checked } : r);
                  const selected = updated.filter(r => r.checked);
                  onResourceCheck?.(uuid, checked, selected);
                }}
                isImage={isImage}
                showFileName={showFileName}
              />
            ))}
          </div>
        </div>

        {/* ===== FOOTER ===== */}
        {!embedded && <div className="bg-[#f4f4f5] dark:bg-dark-bg-primary/50 border-t border-border-primary dark:border-dark-border-primary px-5 py-4 flex items-center justify-end gap-3 shrink-0">
          <Button
            type="plain"
            size="l"
            onClick={onClose}
            className="text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/10"
          >
            Cancelar
          </Button>
          <Button
            type="default"
            size="l"
            onClick={() => onAccept?.(resources.filter(r => r.checked))}
          >
            {acceptButtonText}
          </Button>
        </div>}
      </div>

      {/* ===== CONFIRMATION ALERT ===== */}
      {resourceToDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-[2px] p-4 animate-in fade-in duration-200">
          <AlertDialog
            title="Eliminar recurso"
            confirmText="Eliminar"
            cancelText="Cancelar"
            onConfirm={confirmDelete}
            onCancel={() => setResourceToDelete(null)}
          >
            <p className="text-sm leading-5 font-normal text-content-tertiary dark:text-content-tertiary">
              {`¿Estás seguro de que deseas eliminar "${resourceToDelete.name}"? Esta acción no se puede deshacer.`}
            </p>
          </AlertDialog>
        </div>
      )}

      {/* ===== ERROR ALERT ===== */}
      {internalError && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-[2px] p-4 animate-in fade-in duration-200">
          <AlertDialog
            title={internalError.title}
            confirmText="Entendido"
            onConfirm={() => setInternalError(null)}
          >
            <p className="text-sm leading-5 font-normal text-content-tertiary dark:text-content-tertiary">
              {internalError.message}
            </p>
          </AlertDialog>
        </div>
      )}
    </div>
  );
};
